/**
 * MissileSystem — the Mk V Swarm-lord's homing-missile salvo (a manual skill on a
 * long cooldown). A salvo streams out 2 missiles at a time (one from each wing),
 * 0.5s between batches, until the whole complement is away. Each missile fans to
 * the front, flies straight for a beat, then homes on the ONE enemy it was handed
 * — it never re-targets, and detonates on contact or after MISSILE.life seconds.
 */
import { Vector2 } from "@/math/Vector2";
import { MISSILE } from "@/config/balance";
import type { Damageable } from "./turrets";
import type { MineableBody } from "@/game/world/body";

export interface Missile {
  pos: Vector2;
  vel: Vector2;
  heading: number;
  speed: number;
  target: Damageable | null; // enemy assigned at launch; never re-acquired
  bodyTarget: MineableBody | null; // OR an asteroid/moon to mine (Battleship, no enemy)
  life: number;
  launchTimer: number; // >0 → flying straight out of the tube (no homing yet)
  trail: { x: number; y: number }[];
  isBranch: boolean; // a secondary missile spawned from another's detonation (re-acquires a target)
}

export interface MissileCtx {
  heading: number; // mothership heading — every missile launches straight forward
  // Swarm-lord wings: each wing is a line from its inner root to its outer tip;
  // successive batches launch from progressively-further-out points along it.
  leftInner: { x: number; y: number };
  leftOuter: { x: number; y: number };
  rightInner: { x: number; y: number };
  rightOuter: { x: number; y: number };
  // Mk VI Battleship: explicit silo world-points. When present, this OVERRIDES the
  // wing model — batches stream 2 missiles at a time from successive silos (cycling
  // through the list), so a 6-silo hull fires front pair, mid pair, rear pair, …
  silos?: { x: number; y: number }[];
  enemies: Damageable[];
  // Battleship shoot-to-mine: when a salvo was launched with no enemy in reach, the
  // missiles home on these rocks and the blast MINES them (credits minerals).
  bodies?: MineableBody[];
  onOreMined?: (amount: number) => void;
  onMoonKill?: (b: MineableBody) => void;
  onDetonate: (x: number, y: number) => void; // blast VFX (GameState pushes an explosion)
  onHit: (x: number, y: number) => void; // impact spark
  branchChance: number; // 0..1 chance a hit spawns branch missiles (Swarm-lord / worm only)
  branchCount: number; // how many branch missiles a successful roll spawns
  smart?: boolean; // Mk VI SWL Smart Missiles: re-acquire a new target if the original dies
}

function norm(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

function nearestEnemy(pos: { x: number; y: number }, enemies: Damageable[]): Damageable | null {
  let best: Damageable | null = null;
  let bestD2 = Infinity;
  for (const e of enemies) {
    if (e.hp <= 0) continue;
    const d2 = (e.pos.x - pos.x) ** 2 + (e.pos.y - pos.y) ** 2;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = e;
    }
  }
  return best;
}

export class MissileSystem {
  readonly missiles: Missile[] = [];
  cooldown = 0; // seconds until the salvo can be fired again
  private pending = 0; // missiles left to stream out this salvo
  private salvoSize = 0; // total missiles this salvo (for the wing-position spread)
  private batchIdx = 0; // which batch (0-based) is being fired
  private batchTimer = 0; // time until the next 2-missile batch
  private nextTarget = 0; // index into the salvo's captured target list
  private siloCursor = 0; // Mk VI: which silo the next missile streams from (cycles)
  private targets: Damageable[] = []; // enemies (already range-filtered) at salvo start
  private rockTargets: MineableBody[] = []; // rocks to mine when the salvo had no enemy

  reset(): void {
    this.missiles.length = 0;
    this.cooldown = 0;
    this.pending = 0;
    this.salvoSize = 0;
    this.batchIdx = 0;
    this.batchTimer = 0;
    this.nextTarget = 0;
    this.siloCursor = 0;
    this.targets = [];
    this.rockTargets = [];
  }

  get ready(): boolean {
    return this.cooldown <= 0;
  }

  /** Kick off a salvo of `count` missiles. `enemies` is the caller-supplied in-range
   * list; each missile is handed a distinct one (cycling if there are fewer). If there
   * are no enemies, `bodies` (asteroids/moons) are targeted instead — the salvo mines. */
  launch(count: number, enemies: Damageable[], bodies: MineableBody[] = [], cdMul = 1): boolean {
    if (this.cooldown > 0 || this.pending > 0 || count <= 0) return false;
    this.targets = enemies.filter((e) => e.hp > 0);
    this.rockTargets = this.targets.length > 0 ? [] : bodies.filter((b) => b.ore > 0);
    this.pending = count;
    this.salvoSize = count;
    this.batchIdx = 0;
    this.batchTimer = 0; // first batch immediately
    this.nextTarget = 0;
    this.siloCursor = 0;
    this.cooldown = MISSILE.cooldown * cdMul;
    return true;
  }

  update(dt: number, ctx: MissileCtx): void {
    if (this.cooldown > 0) this.cooldown = Math.max(0, this.cooldown - dt);
    // Stream out the salvo: 2 at a time (one per wing) every batchInterval.
    if (this.pending > 0) {
      this.batchTimer -= dt;
      if (this.batchTimer <= 0) {
        this.spawnBatch(ctx);
        this.batchTimer = MISSILE.batchInterval;
      }
    }
    const hitSq = MISSILE.hitRadius * MISSILE.hitRadius;
    for (let i = this.missiles.length - 1; i >= 0; i--) {
      const mi = this.missiles[i]!;
      mi.life -= dt;
      mi.speed = Math.min(MISSILE.speed, mi.speed + MISSILE.accel * dt);
      // Smart Missiles (Mk VI SWL): a missile whose enemy target died chases a new nearest one.
      if (ctx.smart && !mi.isBranch && mi.target && mi.target.hp <= 0) {
        mi.target = nearestEnemy(mi.pos, ctx.enemies);
      }
      // Home on the assigned target while it lives (enemy OR rock).
      const homePos =
        mi.target && mi.target.hp > 0
          ? mi.target.pos
          : mi.bodyTarget && mi.bodyTarget.ore > 0
            ? mi.bodyTarget.pos
            : null;
      if (mi.launchTimer > 0) {
        mi.launchTimer -= dt; // fly straight out first (no homing)
        // A branch missile finds a NEW target the moment its straight phase ends.
        if (mi.isBranch && mi.launchTimer <= 0 && (!mi.target || mi.target.hp <= 0)) {
          mi.target = nearestEnemy(mi.pos, ctx.enemies);
        }
      } else if (homePos) {
        const desired = Math.atan2(homePos.y - mi.pos.y, homePos.x - mi.pos.x);
        const diff = norm(desired - mi.heading);
        const step = MISSILE.turnRate * dt;
        mi.heading += diff > step ? step : diff < -step ? -step : diff;
      }
      mi.vel.set(Math.cos(mi.heading) * mi.speed, Math.sin(mi.heading) * mi.speed);
      mi.pos.x += mi.vel.x * dt;
      mi.pos.y += mi.vel.y * dt;
      mi.trail.push({ x: mi.pos.x, y: mi.pos.y });
      if (mi.trail.length > 9) mi.trail.shift();
      let hitEnemy = false;
      for (const e of ctx.enemies) {
        if (e.hp <= 0) continue;
        if ((e.pos.x - mi.pos.x) ** 2 + (e.pos.y - mi.pos.y) ** 2 < hitSq) {
          hitEnemy = true;
          break;
        }
      }
      // Only MINING missiles (those handed a rock) collide with asteroids/moons, so
      // enemy-targeted missiles never detonate early on a rock they pass.
      let hitRock = false;
      if (!hitEnemy && mi.bodyTarget && ctx.bodies) {
        for (const b of ctx.bodies) {
          if (b.ore <= 0) continue;
          const rr = b.radius + MISSILE.hitRadius;
          if ((b.pos.x - mi.pos.x) ** 2 + (b.pos.y - mi.pos.y) ** 2 < rr * rr) {
            hitRock = true;
            break;
          }
        }
      }
      if (hitEnemy || hitRock || mi.life <= 0) {
        this.detonate(mi, ctx, hitEnemy);
        this.missiles.splice(i, 1);
      }
    }
  }

  /** Launch one missile from each wing this batch — from a point that walks from the
   * wing root to the tip as the salvo progresses (batch 1 innermost … last outermost).
   * Every missile fires straight forward (no fan). */
  private spawnBatch(ctx: MissileCtx): void {
    const ang = ctx.heading; // straight out the nose
    const vx = Math.cos(ang) * MISSILE.launchSpeed;
    const vy = Math.sin(ang) * MISSILE.launchSpeed;
    const fire = (px: number, py: number) => {
      const target =
        this.targets.length > 0 ? this.targets[this.nextTarget % this.targets.length]! : null;
      // No enemies → hand each missile a rock to mine (round-robin).
      const bodyTarget =
        !target && this.rockTargets.length > 0
          ? this.rockTargets[this.nextTarget % this.rockTargets.length]!
          : null;
      this.nextTarget++;
      this.missiles.push({
        pos: new Vector2(px, py),
        vel: new Vector2(vx, vy),
        heading: ang,
        speed: MISSILE.launchSpeed,
        target,
        bodyTarget,
        life: MISSILE.life,
        launchTimer: MISSILE.launchStraight,
        trail: [],
        isBranch: false,
      });
      this.pending--;
    };
    // Mk VI Battleship: fixed silos — stream up to 2 at a time from successive
    // silos, cycling through the list (front pair, mid pair, rear pair, …).
    if (ctx.silos && ctx.silos.length > 0) {
      const perBatch = Math.min(2, ctx.silos.length); // 1 silo → one-by-one; else a pair
      for (let k = 0; k < perBatch && this.pending > 0; k++) {
        const s = ctx.silos[this.siloCursor % ctx.silos.length]!;
        this.siloCursor++;
        fire(s.x, s.y);
      }
      this.batchIdx++;
      return;
    }
    // Swarm-lord wings: a point that walks from the wing root to the tip as the
    // salvo progresses (batch 1 innermost … last outermost). One per wing.
    const batches = Math.max(1, Math.ceil(this.salvoSize / 2));
    const t = batches <= 1 ? 0 : this.batchIdx / (batches - 1); // 0 (root) → 1 (tip)
    this.batchIdx++;
    const wings: { inner: { x: number; y: number }; outer: { x: number; y: number } }[] = [
      { inner: ctx.leftInner, outer: ctx.leftOuter },
      { inner: ctx.rightInner, outer: ctx.rightOuter },
    ];
    for (const w of wings) {
      if (this.pending <= 0) break;
      fire(w.inner.x + (w.outer.x - w.inner.x) * t, w.inner.y + (w.outer.y - w.inner.y) * t);
    }
  }

  private detonate(mi: Missile, ctx: MissileCtx, hitEnemy: boolean): void {
    const r2 = MISSILE.blastRadius * MISSILE.blastRadius;
    for (const e of ctx.enemies) {
      if (e.hp <= 0) continue;
      if ((e.pos.x - mi.pos.x) ** 2 + (e.pos.y - mi.pos.y) ** 2 < r2) {
        this.damage(e, MISSILE.damage);
        ctx.onHit(e.pos.x, e.pos.y);
      }
    }
    // Battleship shoot-to-mine: a MINING missile's blast carves ore from nearby rocks.
    if (mi.bodyTarget && ctx.bodies) {
      for (const b of ctx.bodies) {
        if (b.ore <= 0) continue;
        const rr = MISSILE.blastRadius + b.radius;
        if ((b.pos.x - mi.pos.x) ** 2 + (b.pos.y - mi.pos.y) ** 2 > rr * rr) continue;
        const before = b.ore;
        const removed = Math.min(before, MISSILE.damage);
        b.ore -= removed;
        ctx.onOreMined?.(removed);
        if (b.ore <= 0 && before > 0 && b.id.startsWith("moon_")) ctx.onMoonKill?.(b);
        ctx.onHit(b.pos.x, b.pos.y);
      }
    }
    ctx.onDetonate(mi.pos.x, mi.pos.y);
    // Branch missiles: a direct enemy hit can split into fresh homing missiles.
    if (!mi.isBranch && hitEnemy && ctx.branchChance > 0 && Math.random() < ctx.branchChance) {
      this.spawnBranch(mi.pos.x, mi.pos.y, ctx.branchCount);
    }
  }

  /** Spawn `count` branch missiles flying out in random directions from a detonation; each
   * flies straight briefly, then homes on the nearest enemy. */
  private spawnBranch(x: number, y: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      this.missiles.push({
        pos: new Vector2(x, y),
        vel: new Vector2(Math.cos(a) * MISSILE.launchSpeed, Math.sin(a) * MISSILE.launchSpeed),
        heading: a,
        speed: MISSILE.launchSpeed,
        target: null,
        bodyTarget: null,
        life: MISSILE.life,
        launchTimer: MISSILE.branchStraightTime,
        trail: [],
        isBranch: true,
      });
    }
  }

  private damage(t: Damageable, amount: number): void {
    let dmg = amount;
    if (t.shield > 0) {
      const a = Math.min(t.shield, dmg);
      t.shield -= a;
      dmg -= a;
    }
    if (dmg > 0) t.hp = Math.max(0, t.hp - dmg);
    t.shieldFlash = 1;
    t.hitTimer = 0;
  }
}
